import { differenceInMinutes, isValid } from 'date-fns';

import {
  MAX_PIPELINE_ATTEMPTS,
  PIPELINE_PENDING_GRACE_MINUTES,
  PIPELINE_REDRIVE_BACKOFF_MINUTES,
  PIPELINE_STALL_THRESHOLD_MINUTES,
} from '../constants/pipeline-retry';
import type { InsightFailureKind } from '../types/insight-node';

interface RedriveSchedule {
  failureKind: InsightFailureKind | null;
  attemptCount: number;
  lastAttemptAt: string | null;
}

/**
 * Minutes until the sweeper re-drives this document, 0 when
 * it is already due, and null when no automatic retry is
 * coming at all.
 *
 * The sweeper's selection and the rail's countdown both
 * resolve here, so a document cannot be told one schedule
 * and re-driven on another.
 */
export function minutesUntilPipelineRedrive(
  { failureKind, attemptCount, lastAttemptAt }: RedriveSchedule,
  now: Date
): number | null {
  if (failureKind !== 'transient' && failureKind !== 'stalled') {
    return null;
  }
  if (attemptCount >= MAX_PIPELINE_ATTEMPTS) return null;

  // These timestamps come out of unvalidated jsonb. An
  // unparseable one would otherwise render as
  // "retrying in NaN min".
  const lastAttempt = lastAttemptAt ? new Date(lastAttemptAt) : null;
  if (!lastAttempt || !isValid(lastAttempt)) return 0;

  const backoffIndex = Math.min(
    Math.max(attemptCount - 1, 0),
    PIPELINE_REDRIVE_BACKOFF_MINUTES.length - 1
  );
  const elapsed = differenceInMinutes(now, lastAttempt);
  return Math.max(PIPELINE_REDRIVE_BACKOFF_MINUTES[backoffIndex] - elapsed, 0);
}

/**
 * True when a run has gone quiet long enough that it is
 * presumed dead.
 *
 * A missing or unparseable heartbeat counts as stale. The
 * stamp comes out of unvalidated jsonb, and a garbage one is
 * no more evidence of life than none at all — reading it as
 * alive would leave the document in `processing` forever,
 * which is the state this exists to clear.
 */
export function isPipelineRunStale(
  stageStartedAt: string | null,
  now: Date
): boolean {
  if (!stageStartedAt) return true;
  const startedAt = new Date(stageStartedAt);
  if (!isValid(startedAt)) return true;
  return differenceInMinutes(now, startedAt) > PIPELINE_STALL_THRESHOLD_MINUTES;
}

/**
 * True when a queued document has waited long enough that its
 * trigger is presumed lost.
 *
 * A re-drive writes `pending` and then sends the event; if the
 * send fails, nothing else would ever move the row again —
 * stall detection only looks at `processing` and re-drive only
 * at `failed`. This is what keeps `pending` from being a hole
 * documents fall into.
 */
export function isPipelineRunAbandoned(
  queuedSince: string | null,
  now: Date
): boolean {
  if (!queuedSince) return false;
  const since = new Date(queuedSince);
  if (!isValid(since)) return false;
  return differenceInMinutes(now, since) > PIPELINE_PENDING_GRACE_MINUTES;
}
